import React, { useEffect, useState, useCallback } from 'react';
import AdminInsights, { TopUsersCard } from '../components/admin/AdminInsights';
import { format, formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import { API_URL, getAuthHeader, formatFileSize } from '../utils/config';
import useStore from '../store/useStore';
import axios from 'axios';

const api = (path, opts={}) => axios({ url:`${API_URL}${path}`, headers:getAuthHeader(), ...opts });

// Simple bar chart component
const BarChart = ({ data, color='#0d9488', height=120, label='count' }) => {
  if (!data?.length) return <div className="flex items-center justify-center h-24 text-gray-400 text-xs">No data yet</div>;
  const max = Math.max(...data.map(d => Number(d[label]||d.count||0)), 1);
  return (
    <div className="flex items-end gap-1" style={{height}}>
      {data.map((d, i) => {
        const val = Number(d[label]||d.count||0);
        const h = Math.max((val/max)*height, val > 0 ? 4 : 1);
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition whitespace-nowrap z-10">
              {val}
            </div>
            <div className="w-full rounded-t-md transition-all" style={{height:`${h}px`, background:color, opacity:0.85}}/>
            {d.date && <span className="text-gray-400 rotate-45 origin-left" style={{fontSize:'8px'}}>{format(new Date(d.date),'M/d')}</span>}
            {d.month && <span className="text-gray-400" style={{fontSize:'8px'}}>{d.month}</span>}
            {d.hour !== undefined && <span className="text-gray-400" style={{fontSize:'8px'}}>{d.hour}h</span>}
          </div>
        );
      })}
    </div>
  );
};

// Gauge/donut
const Gauge = ({ value, max, color, label, sublabel }) => {
  const pct = Math.min((value/max)*100, 100);
  const r = 36, circ = 2*Math.PI*r;
  const dash = (pct/100)*circ;
  return (
    <div className="flex flex-col items-center">
      <div className="relative">
        <svg width="96" height="96" viewBox="0 0 96 96">
          <circle cx="48" cy="48" r={r} fill="none" stroke="currentColor" strokeWidth="8" className="text-gray-100 dark:text-gray-700"/>
          <circle cx="48" cy="48" r={r} fill="none" stroke={color} strokeWidth="8"
            strokeDasharray={`${dash} ${circ}`} strokeDashoffset={circ*0.25}
            strokeLinecap="round" style={{transition:'stroke-dasharray 0.8s ease'}}/>
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-lg font-black text-gray-800 dark:text-white">{Math.round(pct)}%</span>
        </div>
      </div>
      <p className="text-xs font-bold text-gray-600 dark:text-gray-300 mt-1">{label}</p>
      <p className="text-xs text-gray-400">{sublabel}</p>
    </div>
  );
};

export default function AdminPage() {
  const { user, darkMode } = useStore();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [auditType, setAuditType] = useState('all');
  const [settings, setSettings] = useState({});
  const [msgStats, setMsgStats] = useState(null);
  const [archiveDays, setArchiveDays] = useState(180);
  const [deleteDays, setDeleteDays] = useState(365);
  const [processing, setProcessing] = useState(false);
  const [fileSizeLimit, setFileSizeLimit] = useState(100);
  const [confirmModal, setConfirmModal] = useState(null);
  // confirmModal: { title, message, details, type:'danger'|'warning', onConfirm }
  const [loading, setLoading] = useState(true);
  const [userSearch, setUserSearch] = useState('');
  const [resetModal, setResetModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [healthInterval, setHealthInterval] = useState(null);

  const fetchStats = useCallback(async () => {
    try { const { data } = await api('/admin/stats'); setStats(data); } catch(e) {}
  }, []);

  const fetchHealth = useCallback(async () => {
    try { const { data } = await api('/admin/health'); setHealth(data); } catch(e) {}
  }, []);

  const fetchUsers = useCallback(async () => {
    try { const { data } = await api('/admin/users'); setUsers(data); } catch(e) {}
  }, []);

  const fetchAudit = useCallback(async () => {
    try { const { data } = await api(`/admin/audit?type=${auditType}&limit=100`); setAudit(data); } catch(e) {}
  }, [auditType]);

  useEffect(() => {
    Promise.all([fetchStats(), fetchUsers()]).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (activeTab === 'health') {
      fetchHealth();
      const iv = setInterval(fetchHealth, 10000);
      setHealthInterval(iv);
      return () => clearInterval(iv);
    } else {
      if (healthInterval) clearInterval(healthInterval);
    }
  }, [activeTab]);

  useEffect(() => { if (activeTab === 'audit') fetchAudit(); }, [activeTab, auditType]);
  // Auto-refresh dashboard stats every 30 seconds to keep online count live
  useEffect(() => {
    if (activeTab !== 'dashboard') return;
    const interval = setInterval(() => {
      api('/admin/stats').then(({data}) => setStats(data)).catch(()=>{});
    }, 30000);
    return () => clearInterval(interval);
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'settings') {
      api('/admin/settings').then(({data}) => {
        setSettings(data);
        setFileSizeLimit(Number(data.max_file_size_mb)||100);
        // Load saved archive/delete policy — 0 means Never
        if (data.auto_archive_days !== undefined) setArchiveDays(Number(data.auto_archive_days));
        if (data.auto_delete_days !== undefined) setDeleteDays(Number(data.auto_delete_days));
      }).catch(()=>{});
      api('/admin/messages/stats').then(({data}) => setMsgStats(data)).catch(()=>{});
    }
  }, [activeTab]);

  const toggleUser = async (id) => {
    try {
      const { data } = await api(`/admin/users/${id}/toggle`, { method:'PUT' });
      toast.success(data.message);
      fetchUsers();
    } catch(e) { toast.error('Failed'); }
  };

  const toggleAdmin = async (id) => {
    try {
      const { data } = await api(`/admin/users/${id}/admin`, { method:'PUT' });
      toast.success(data.message);
      fetchUsers();
    } catch(e) { toast.error('Failed'); }
  };

  const resetPassword = async () => {
    if (!newPassword || newPassword.length < 6) return toast.error('Min 6 characters');
    try {
      await api(`/admin/users/${resetModal.id}/reset-password`, { method:'PUT', data:{ password:newPassword } });
      toast.success('Password reset!');
      setResetModal(null); setNewPassword('');
    } catch(e) { toast.error('Failed'); }
  };

  const exportUsersCSV = () => {
    const esc = (v) => {
      const s = (v === null || v === undefined) ? '' : String(v);
      return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    const fmt = (d) => d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '';
    const headers = ['Name','Email','Department','Designation','Team','Role','Messages','Conversations','Status','Last Seen','Joined'];
    const rows = filteredUsers.map(u => [
      u.full_name, u.email, u.department, u.designation, u.team,
      u.is_admin ? 'Admin' : 'Member',
      u.message_count, u.conversation_count,
      u.is_active ? 'Active' : 'Disabled',
      fmt(u.last_seen), fmt(u.created_at)
    ].map(esc).join(','));
    // BOM so Excel opens UTF-8 names correctly
    const csv = '\uFEFF' + [headers.join(','), ...rows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `qtalk-users-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const filteredUsers = users.filter(u =>
    u.full_name?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.department?.toLowerCase().includes(userSearch.toLowerCase())
  );

  const tabs = [
    { id:'dashboard', label:'Dashboard', icon:<path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/> },
    { id:'users',     label:'Users',     icon:<><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></> },
    { id:'health',    label:'Server',    icon:<><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></> },
    { id:'audit',     label:'Audit Log', icon:<><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></> },
    { id:'settings',  label:'Settings',   icon:<><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></> },
  ];

  if (loading) return (
    <div className={`fixed inset-0 flex items-center justify-center ${darkMode?'dark bg-gray-950':'bg-gray-50'}`}>
      <div className="flex flex-col items-center gap-3">
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{background:'linear-gradient(135deg,#0d9488,#2563eb)'}}>
          <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
        </div>
        <div className="w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"/>
      </div>
    </div>
  );

  return (
    <div className={`fixed inset-0 flex ${darkMode?'dark':''}`} style={{background: darkMode?'#080d14':'#f1f5f9'}}>

      {/* ── SIDEBAR ── */}
      <div className="w-56 flex-shrink-0 flex flex-col"
        style={{background: darkMode?'#0d1420':'white',
          borderRight: darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid rgba(0,0,0,0.06)'}}>

        {/* Logo */}
        <div className="px-5 py-5 flex items-center gap-3"
          style={{borderBottom: darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid rgba(0,0,0,0.06)'}}>
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{background:'linear-gradient(135deg,#0d9488,#2563eb)'}}>
            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
          </div>
          <div>
            <p className="font-black text-gray-900 dark:text-white text-sm">Admin Panel</p>
            <p className="text-xs text-gray-400">Qtalk · QualTechEdge</p>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-3 space-y-1">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all"
              style={activeTab===tab.id
                ? {background:'linear-gradient(135deg,#0d9488,#2563eb)', color:'white', boxShadow:'0 4px 12px rgba(13,148,136,0.3)'}
                : {color: darkMode?'rgba(255,255,255,0.5)':'#6b7280', background:'transparent'}}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0">
                {tab.icon}
              </svg>
              {tab.label}
            </button>
          ))}
        </nav>

        {/* Bottom */}
        <div className="p-3 space-y-1" style={{borderTop: darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid rgba(0,0,0,0.06)'}}>
          <a href="/chat"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all"
            style={{color: darkMode?'rgba(255,255,255,0.4)':'#6b7280'}}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
              <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
            </svg>
            Back to Chat
          </a>
        </div>
      </div>

      {/* ── MAIN ── */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between flex-shrink-0"
          style={{background: darkMode?'rgba(13,20,32,0.95)':'rgba(255,255,255,0.95)',
            backdropFilter:'blur(12px)',
            borderBottom: darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid rgba(0,0,0,0.06)'}}>
          <div>
            <h1 className="text-xl font-black text-gray-900 dark:text-white">
              {tabs.find(t=>t.id===activeTab)?.label}
            </h1>
            <p className="text-xs text-gray-400">
              {activeTab==='dashboard' && `${stats?.total_users||0} users · ${stats?.active_today||0} active today`}
              {activeTab==='users' && `${users.length} total users`}
              {activeTab==='health' && (health ? `${health.hostname} · ${health.platform}` : 'Fetching...')}
              {activeTab==='audit' && `Last 100 events`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white text-xs font-bold">
              {user?.full_name?.[0]?.toUpperCase()}
            </div>
            <div>
              <p className="text-xs font-bold text-gray-700 dark:text-gray-200">{user?.full_name}</p>
              <p className="text-xs text-teal-500 font-medium">Super Admin</p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">

          {/* ══ DASHBOARD ══ */}
          {activeTab==='dashboard' && stats && (
            <div className="space-y-6">
              {/* KPI Cards */}
              <div className="grid grid-cols-5 gap-4">
                {[
                  { label:'Total Users',     value:stats.total_users,        sub:`${stats.active_today} active today`,  gradient:'135deg,#0d9488,#0369a1', icon:<><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></> },
                  { label:'Currently Online', value:stats.currently_online||0, sub:'Live right now', gradient:'135deg,#16a34a,#15803d', pulse:true, icon:<><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></> },
                  { label:'Total Messages',  value:stats.total_messages.toLocaleString(), sub:'All time',            gradient:'135deg,#7c3aed,#4f46e5', icon:<path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/> },
                  { label:'Storage Used',    value:formatFileSize(stats.total_file_size), sub:`${stats.total_files} files`, gradient:'135deg,#f59e0b,#d97706', icon:<><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></> },
                  { label:'Meetings Held',   value:stats.total_meetings,     sub:`${stats.total_todos} tasks`,         gradient:'135deg,#10b981,#059669', icon:<><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></> },
                  { label:'Notes Saved',     value:stats.total_notes||0,     sub:`${stats.notes_users||0} users writing`, gradient:'135deg,#db2777,#be185d', icon:<><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></> },
                ].map(k => (
                  <div key={k.label} className="relative rounded-2xl p-5 text-white overflow-hidden shadow-lg"
                    style={{background:`linear-gradient(${k.gradient})`}}>
                    {k.pulse && <span className="absolute top-3 right-3 flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span><span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span></span>}
                    <div className="absolute -right-4 -bottom-4 opacity-15">
                      <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="w-24 h-24">{k.icon}</svg>
                    </div>
                    <p className="text-2xl font-black">{k.value}</p>
                    <p className="text-sm font-bold text-white/80 mt-0.5">{k.label}</p>
                    <p className="text-xs text-white/50 mt-1">{k.sub}</p>
                  </div>
                ))}
              </div>

              {/* Charts row */}
              <div className="grid grid-cols-3 gap-4">
                {/* Messages per day */}
                <div className="col-span-2 rounded-2xl p-5"
                  style={{background:darkMode?'rgba(255,255,255,0.03)':'white',
                    border:darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid rgba(0,0,0,0.06)'}}>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="font-black text-gray-800 dark:text-white text-sm">Message Activity</p>
                      <p className="text-xs text-gray-400">Last 30 days</p>
                    </div>
                    <span className="text-xs font-bold px-2 py-1 rounded-lg" style={{background:'rgba(13,148,136,0.1)',color:'#0d9488'}}>
                      Msgs/day
                    </span>
                  </div>
                  <BarChart data={stats.messages_per_day} color="#0d9488" height={140}/>
                </div>

                {/* Active users */}
                <div className="rounded-2xl p-5"
                  style={{background:darkMode?'rgba(255,255,255,0.03)':'white',
                    border:darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid rgba(0,0,0,0.06)'}}>
                  <p className="font-black text-gray-800 dark:text-white text-sm mb-1">Active Users</p>
                  <p className="text-xs text-gray-400 mb-4">Last 7 days</p>
                  <BarChart data={stats.active_per_day} color="#7c3aed" height={140}/>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                {/* Today's hourly activity */}
                <div className="rounded-2xl p-5"
                  style={{background:darkMode?'rgba(255,255,255,0.03)':'white',
                    border:darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid rgba(0,0,0,0.06)'}}>
                  <p className="font-black text-gray-800 dark:text-white text-sm mb-1">Today by Hour</p>
                  <p className="text-xs text-gray-400 mb-4">Messages sent today</p>
                  <BarChart data={stats.messages_per_hour} color="#f59e0b" height={100} label="count"/>
                </div>

                {/* Top users — 7d / 30d / All time */}
                <TopUsersCard darkMode={darkMode} stats7d={stats.top_users} />

                {/* Breakdown */}
                <div className="rounded-2xl p-5"
                  style={{background:darkMode?'rgba(255,255,255,0.03)':'white',
                    border:darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid rgba(0,0,0,0.06)'}}>
                  <p className="font-black text-gray-800 dark:text-white text-sm mb-4">Platform Breakdown</p>
                  <div className="space-y-3">
                    {[
                      { label:'Direct Chats',  val:stats.conv_breakdown?.find(c=>c.type==='direct')?.count||0,  color:'#0d9488' },
                      { label:'Group Chats',   val:stats.conv_breakdown?.find(c=>c.type==='group')?.count||0,   color:'#7c3aed' },
                      { label:'Announcements', val:stats.total_announcements, color:'#f59e0b' },
                      { label:'Active Week',   val:stats.active_this_week,    color:'#10b981' },
                    ].map(item => {
                      const max = Math.max(stats.total_conversations, stats.total_announcements, stats.active_this_week, 1);
                      return (
                        <div key={item.label}>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="font-semibold text-gray-600 dark:text-gray-300">{item.label}</span>
                            <span className="font-black" style={{color:item.color}}>{item.val}</span>
                          </div>
                          <div className="h-1.5 rounded-full" style={{background:darkMode?'rgba(255,255,255,0.08)':'#e5e7eb'}}>
                            <div className="h-full rounded-full transition-all"
                              style={{width:`${Math.max((item.val/max)*100,2)}%`, background:item.color}}/>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Extra insights */}
              <AdminInsights darkMode={darkMode} />

              {/* New users per month */}
              {stats.new_users_per_month?.length > 0 && (
                <div className="rounded-2xl p-5"
                  style={{background:darkMode?'rgba(255,255,255,0.03)':'white',
                    border:darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid rgba(0,0,0,0.06)'}}>
                  <p className="font-black text-gray-800 dark:text-white text-sm mb-1">New User Registrations</p>
                  <p className="text-xs text-gray-400 mb-4">Last 6 months</p>
                  <BarChart data={stats.new_users_per_month} color="#10b981" height={100} label="count"/>
                </div>
              )}
            </div>
          )}

          {/* ══ USERS ══ */}
          {activeTab==='users' && (
            <div className="space-y-4">
              {/* Search */}
              <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-sm">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input type="text" placeholder="Search users..." value={userSearch} onChange={e=>setUserSearch(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 transition"
                    style={{background:darkMode?'rgba(255,255,255,0.05)':'white',
                      border:darkMode?'1px solid rgba(255,255,255,0.08)':'1px solid rgba(0,0,0,0.08)',
                      color:darkMode?'white':'#0f172a'}}/>
                </div>
                <span className="text-xs text-gray-400 font-medium">{filteredUsers.length} of {users.length} users</span>
                <button onClick={exportUsersCSV}
                  className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold text-white transition hover:opacity-90"
                  style={{background:'linear-gradient(135deg,#0d9488,#2563eb)'}}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  Export CSV ({filteredUsers.length})
                </button>
              </div>

              {/* Users table */}
              <div className="rounded-2xl overflow-hidden shadow-sm"
                style={{background:darkMode?'rgba(255,255,255,0.03)':'white',
                  border:darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid rgba(0,0,0,0.06)'}}>
                <table className="w-full">
                  <thead>
                    <tr style={{background:darkMode?'rgba(255,255,255,0.04)':'#f8fafc',
                      borderBottom:darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid rgba(0,0,0,0.06)'}}>
                      {['User','Department','Role','Messages','Last Seen','Status','Actions'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-black text-gray-400 uppercase tracking-widest">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u, i) => (
                      <tr key={u.id}
                        className="transition-colors hover:bg-teal-50/30 dark:hover:bg-white/[0.02]"
                        style={{borderBottom:darkMode?'1px solid rgba(255,255,255,0.04)':'1px solid rgba(0,0,0,0.04)'}}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0 overflow-hidden">
                              {u.avatar_url?.startsWith('https://')
                                ? <img src={u.avatar_url?.startsWith("https://") ? u.avatar_url : `https://qtalk.qualtechedge.in${u.avatar_url}`} className="w-full h-full object-cover" alt=""/>
                                : u.full_name?.[0]?.toUpperCase()}
                            </div>
                            <div>
                              <p className="text-sm font-bold text-gray-800 dark:text-white">{u.full_name}</p>
                              <p className="text-xs text-gray-400">{u.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-xs text-gray-600 dark:text-gray-300">{u.department||'—'}</p>
                          <p className="text-xs text-gray-400">{u.designation||''}</p>
                        </td>
                        <td className="px-4 py-3">
                          {u.is_admin
                            ? <span className="text-xs font-bold px-2 py-1 rounded-lg" style={{background:'rgba(245,158,11,0.1)',color:'#f59e0b'}}>Admin</span>
                            : <span className="text-xs text-gray-400">Member</span>}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm font-bold text-gray-700 dark:text-gray-200">{u.message_count}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-gray-400">
                            {u.last_seen ? formatDistanceToNow(new Date(u.last_seen), {addSuffix:true}) : 'Never'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs font-bold px-2 py-1 rounded-lg"
                            style={u.is_active
                              ? {background:'rgba(16,185,129,0.1)',color:'#10b981'}
                              : {background:'rgba(239,68,68,0.1)',color:'#ef4444'}}>
                            {u.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button onClick={() => toggleUser(u.id)}
                              className="text-xs px-2 py-1 rounded-lg font-semibold transition"
                              style={{background:darkMode?'rgba(255,255,255,0.05)':'#f1f5f9',
                                color:u.is_active?'#ef4444':'#10b981'}}>
                              {u.is_active?'Disable':'Enable'}
                            </button>
                            <button onClick={() => toggleAdmin(u.id)}
                              className="text-xs px-2 py-1 rounded-lg font-semibold transition"
                              style={{background:darkMode?'rgba(255,255,255,0.05)':'#f1f5f9',
                                color:'#f59e0b'}}>
                              {u.is_admin?'Revoke':'Admin'}
                            </button>
                            <button onClick={() => { setResetModal(u); setNewPassword(''); }}
                              className="text-xs px-2 py-1 rounded-lg font-semibold transition"
                              style={{background:darkMode?'rgba(255,255,255,0.05)':'#f1f5f9',
                                color:'#6b7280'}}>
                              Reset PW
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ══ SERVER HEALTH ══ */}
          {activeTab==='health' && (
            <div className="space-y-5">
              {!health ? (
                <div className="flex items-center justify-center py-20">
                  <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"/>
                </div>
              ) : (
                <>
                  {/* Auto-refresh indicator */}
                  <div className="flex items-center gap-2 text-xs text-teal-500 font-semibold">
                    <span className="w-2 h-2 bg-teal-500 rounded-full animate-pulse"/>
                    Live monitoring — refreshes every 10 seconds
                  </div>

                  {/* Gauges */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="rounded-2xl p-5 flex flex-col items-center"
                      style={{background:darkMode?'rgba(255,255,255,0.03)':'white',
                        border:darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid rgba(0,0,0,0.06)'}}>
                      <Gauge value={health.memory.percent} max={100}
                        color={health.memory.percent>85?'#ef4444':health.memory.percent>60?'#f59e0b':'#10b981'}
                        label="RAM Usage"
                        sublabel={`${formatFileSize(health.memory.used)} / ${formatFileSize(health.memory.total)}`}/>
                    </div>
                    <div className="rounded-2xl p-5 flex flex-col items-center"
                      style={{background:darkMode?'rgba(255,255,255,0.03)':'white',
                        border:darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid rgba(0,0,0,0.06)'}}>
                      <Gauge
                        value={health.cpu.load_1m} max={health.cpu.count}
                        color={health.cpu.load_1m/health.cpu.count>0.8?'#ef4444':health.cpu.load_1m/health.cpu.count>0.5?'#f59e0b':'#0d9488'}
                        label="CPU Load (1m)"
                        sublabel={`${health.cpu.count} cores · ${health.cpu.load_1m.toFixed(2)}`}/>
                    </div>
                    <div className="rounded-2xl p-5 flex flex-col items-center"
                      style={{background:darkMode?'rgba(255,255,255,0.03)':'white',
                        border:darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid rgba(0,0,0,0.06)'}}>
                      {health.disk.total > 0
                        ? <Gauge value={health.disk.used} max={health.disk.total}
                            color={health.disk.used/health.disk.total>0.9?'#ef4444':health.disk.used/health.disk.total>0.7?'#f59e0b':'#7c3aed'}
                            label="Disk Usage"
                            sublabel={`${formatFileSize(health.disk.used)} / ${formatFileSize(health.disk.total)}`}/>
                        : <div className="text-center"><p className="text-sm font-bold text-gray-500">Disk</p><p className="text-xs text-gray-400">N/A</p></div>
                      }
                    </div>
                    <div className="rounded-2xl p-5 flex flex-col items-center justify-center gap-3"
                      style={{background:darkMode?'rgba(255,255,255,0.03)':'white',
                        border:darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid rgba(0,0,0,0.06)'}}>
                      <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{background:'rgba(13,148,136,0.1)'}}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
                          <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
                        </svg>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-black text-gray-800 dark:text-white">
                          {Math.floor(health.uptime/86400)}d {Math.floor((health.uptime%86400)/3600)}h
                        </p>
                        <p className="text-xs font-bold text-gray-500">Server Uptime</p>
                        <p className="text-xs text-gray-400 mt-1">{health.hostname}</p>
                      </div>
                    </div>
                  </div>

                  {/* Load averages */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="rounded-2xl p-5"
                      style={{background:darkMode?'rgba(255,255,255,0.03)':'white',
                        border:darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid rgba(0,0,0,0.06)'}}>
                      <p className="font-black text-gray-800 dark:text-white text-sm mb-4">CPU Load Averages</p>
                      <div className="space-y-3">
                        {[
                          {label:'1 minute',  val:health.cpu.load_1m},
                          {label:'5 minutes', val:health.cpu.load_5m},
                          {label:'15 minutes',val:health.cpu.load_15m},
                        ].map(l => (
                          <div key={l.label}>
                            <div className="flex justify-between text-xs mb-1">
                              <span className="text-gray-500 dark:text-gray-400 font-medium">{l.label}</span>
                              <span className="font-black text-gray-700 dark:text-gray-200">{l.val.toFixed(2)}</span>
                            </div>
                            <div className="h-2 rounded-full" style={{background:darkMode?'rgba(255,255,255,0.08)':'#e5e7eb'}}>
                              <div className="h-full rounded-full transition-all"
                                style={{
                                  width:`${Math.min((l.val/health.cpu.count)*100,100)}%`,
                                  background: l.val/health.cpu.count > 0.8 ? '#ef4444' : l.val/health.cpu.count > 0.5 ? '#f59e0b' : '#0d9488'
                                }}/>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* DB stats */}
                    <div className="rounded-2xl p-5"
                      style={{background:darkMode?'rgba(255,255,255,0.03)':'white',
                        border:darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid rgba(0,0,0,0.06)'}}>
                      <div className="flex items-center justify-between mb-4">
                        <p className="font-black text-gray-800 dark:text-white text-sm">Database</p>
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 bg-green-400 rounded-full"/>
                          <span className="text-xs font-bold text-green-500">Connected</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        {[
                          {label:'Size',        val:health.db.size},
                          {label:'Connections', val:health.db.active_connections},
                        ].map(s => (
                          <div key={s.label} className="rounded-xl p-3 text-center"
                            style={{background:darkMode?'rgba(255,255,255,0.04)':'#f8fafc'}}>
                            <p className="text-lg font-black text-gray-800 dark:text-white">{s.val}</p>
                            <p className="text-xs text-gray-400">{s.label}</p>
                          </div>
                        ))}
                      </div>
                      <div className="space-y-1.5">
                        {health.db.tables?.map(t => (
                          <div key={t.table} className="flex items-center justify-between text-xs">
                            <span className="font-mono text-gray-500 dark:text-gray-400">{t.table}</span>
                            <span className="font-bold text-gray-700 dark:text-gray-300">{Number(t.live_rows).toLocaleString()} rows</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ══ SETTINGS ══ */}
          {activeTab==='settings' && (
            <div className="p-6 space-y-6 max-w-3xl">
              <div>
                <h2 className="text-xl font-black text-gray-800 dark:text-white">Platform Settings</h2>
                <p className="text-sm text-gray-400 mt-1">Configure file limits, message retention and storage</p>
              </div>

              {/* ── FILE UPLOAD CARD ── */}
              <div className="rounded-3xl overflow-hidden" style={{background:'linear-gradient(135deg,rgba(13,148,136,0.08),rgba(37,99,235,0.05))',border:'1.5px solid rgba(13,148,136,0.2)'}}>
                {/* Card header */}
                <div className="px-6 py-5 flex items-center gap-4" style={{background:'linear-gradient(135deg,rgba(13,148,136,0.15),rgba(37,99,235,0.1))'}}>
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg" style={{background:'linear-gradient(135deg,#0d9488,#2563eb)'}}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  </div>
                  <div className="flex-1">
                    <p className="font-black text-gray-800 dark:text-white text-base">File Upload Controls</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Set size limits and restrict file types across the platform</p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-black" style={{background:'linear-gradient(135deg,#0d9488,#2563eb)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}}>{fileSizeLimit >= 1024 ? `${(fileSizeLimit/1024).toFixed(1)} GB` : `${fileSizeLimit} MB`}</p>
                    <p className="text-xs text-gray-400">current limit</p>
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  {/* Size presets grid */}
                  <div>
                    <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-3">Quick Presets</p>
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        {val:10,label:'10 MB',desc:'Docs only'},
                        {val:50,label:'50 MB',desc:'Standard'},
                        {val:100,label:'100 MB',desc:'Recommended'},
                        {val:250,label:'250 MB',desc:'Large files'},
                        {val:512,label:'512 MB',desc:'Half GB'},
                        {val:1024,label:'1 GB',desc:'Power users'},
                        {val:2048,label:'2 GB',desc:'Media heavy'},
                        {val:5120,label:'5 GB',desc:'Enterprise'},
                      ].map(p => (
                        <button key={p.val} onClick={() => {
                          setFileSizeLimit(p.val);
                          api('/admin/settings/max_file_size_mb',{method:'put',data:{value:p.val}})
                            .then(()=>toast.success(`✅ File limit set to ${p.label}`))
                            .catch(()=>toast.error('Failed to save'));
                        }}
                          className="p-3 rounded-2xl text-center transition-all"
                          style={fileSizeLimit===p.val
                            ? {background:'linear-gradient(135deg,#0d9488,#2563eb)',boxShadow:'0 4px 15px rgba(13,148,136,0.4)'}
                            : {background:'rgba(255,255,255,0.05)',border:'1.5px solid rgba(255,255,255,0.08)'}}>
                          <p className={`text-sm font-black ${fileSizeLimit===p.val?'text-white':'text-gray-700 dark:text-gray-200'}`}>{p.label}</p>
                          <p className={`text-xs mt-0.5 ${fileSizeLimit===p.val?'text-white/70':'text-gray-400'}`}>{p.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Custom input + slider */}
                  <div className="p-4 rounded-2xl" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
                    <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-3">Custom Size</p>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <input type="number" min="1" max="51200" value={fileSizeLimit}
                          onChange={e => setFileSizeLimit(Math.max(1, Math.min(51200, Number(e.target.value))))}
                          onBlur={e => {
                            const val = Math.max(1, Math.min(51200, Number(e.target.value)));
                            api('/admin/settings/max_file_size_mb',{method:'put',data:{value:val}})
                              .then(()=>toast.success(`✅ File limit set to ${val >= 1024 ? (val/1024).toFixed(1)+'GB' : val+'MB'}`))
                              .catch(()=>toast.error('Failed'));
                          }}
                          className="w-24 px-3 py-2.5 rounded-xl text-sm font-black text-center focus:outline-none focus:ring-2 focus:ring-teal-400 dark:bg-gray-700 dark:text-white border"
                          style={{borderColor:'rgba(13,148,136,0.5)'}}/>
                        <select value={fileSizeLimit >= 1024 ? 'gb' : 'mb'}
                          onChange={e => {
                            if (e.target.value === 'gb') setFileSizeLimit(Math.max(1, Math.round(fileSizeLimit/1024)) * 1024);
                            else setFileSizeLimit(Math.max(1, fileSizeLimit < 1024 ? fileSizeLimit : Math.round(fileSizeLimit/1024)));
                          }}
                          className="px-3 py-2.5 rounded-xl text-sm font-black focus:outline-none dark:bg-gray-700 dark:text-white border"
                          style={{borderColor:'rgba(13,148,136,0.5)'}}>
                          <option value="mb">MB</option>
                          <option value="gb">GB</option>
                        </select>
                      </div>
                      <input type="range" min="1" max="10240" step="1" value={Math.min(fileSizeLimit, 10240)}
                        onChange={e => setFileSizeLimit(Number(e.target.value))}
                        className="flex-1 h-2 rounded-full appearance-none cursor-pointer" style={{accentColor:'#0d9488'}}/>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">Range: 1 MB — 10 GB (enter manually for larger)</p>
                  </div>

                  {/* File type restriction */}
                  <div>
                    <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-3">File Type Policy</p>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        {id:'all',label:'Allow All Files',desc:'No restrictions',icon:'📁',color:'#0d9488'},
                        {id:'no_exe',label:'Block Executables',desc:'No .exe .bat .sh etc',icon:'🛡️',color:'#2563eb'},
                        {id:'docs',label:'Docs & Images',desc:'PDF, Office, images',icon:'📄',color:'#7c3aed'},
                        {id:'images',label:'Images Only',desc:'jpg, png, gif, webp',icon:'🖼️',color:'#db2777'},
                        {id:'media',label:'Media Only',desc:'Images + audio + video',icon:'🎬',color:'#d97706'},
                        {id:'none',label:'Disable Uploads',desc:'No file sharing',icon:'🚫',color:'#ef4444'},
                      ].map(opt => (
                        <button key={opt.id}
                          onClick={() => {
                            api('/admin/settings/allowed_file_types',{method:'put',data:{value:opt.id}})
                              .then(()=>{setSettings(s=>({...s,allowed_file_types:opt.id}));toast.success('File policy updated!');})
                              .catch(()=>toast.error('Failed'));
                          }}
                          className="flex items-center gap-3 p-3.5 rounded-2xl text-left transition-all"
                          style={settings.allowed_file_types===opt.id
                            ? {background:`${opt.color}15`,border:`2px solid ${opt.color}`,boxShadow:`0 4px 15px ${opt.color}20`}
                            : {background:'rgba(255,255,255,0.03)',border:'1.5px solid rgba(255,255,255,0.06)'}}>
                          <span className="text-2xl flex-shrink-0">{opt.icon}</span>
                          <div className="min-w-0">
                            <p className="text-xs font-black truncate" style={{color:settings.allowed_file_types===opt.id?opt.color:'inherit'}}>{opt.label}</p>
                            <p className="text-xs text-gray-400 truncate">{opt.desc}</p>
                          </div>
                          {settings.allowed_file_types===opt.id && (
                            <svg viewBox="0 0 24 24" fill="none" stroke={opt.color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 ml-auto flex-shrink-0"><polyline points="20 6 9 17 4 12"/></svg>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button onClick={() => {
                      api('/admin/settings/max_file_size_mb',{method:'put',data:{value:fileSizeLimit}})
                        .then(()=>toast.success(`✅ File limit saved: ${fileSizeLimit >= 1024 ? `${(fileSizeLimit/1024).toFixed(1)} GB` : `${fileSizeLimit} MB`}`))
                        .catch(()=>toast.error('Failed'));
                    }}
                    className="w-full py-3 text-sm font-black text-white rounded-2xl hover:opacity-90 transition shadow-lg"
                    style={{background:'linear-gradient(135deg,#0d9488,#2563eb)',boxShadow:'0 4px 20px rgba(13,148,136,0.3)'}}>
                    💾 Save File Upload Settings
                  </button>
                </div>
              </div>

              {/* ── MESSAGE STORAGE CARD ── */}
              <div className="rounded-3xl overflow-hidden" style={{background:'rgba(255,255,255,0.02)',border:'1.5px solid rgba(255,255,255,0.08)'}}>
                {/* Stats header */}
                {msgStats && (
                  <div className="grid grid-cols-4 gap-0 border-b" style={{borderColor:'rgba(255,255,255,0.06)'}}>
                    {[
                      {label:'Total Messages',val:Number(msgStats.total).toLocaleString(),icon:'💬',color:'#0d9488'},
                      {label:'DB Size',val:msgStats.table_size,icon:'🗄️',color:'#2563eb'},
                      {label:'>6 months old',val:Number(msgStats.older_180d).toLocaleString(),icon:'📅',color:'#f59e0b'},
                      {label:'>1 year old',val:Number(msgStats.older_365d).toLocaleString(),icon:'⏳',color:'#ef4444'},
                    ].map((s,i) => (
                      <div key={s.label} className={`p-5 text-center ${i<3?'border-r':''}`} style={{borderColor:'rgba(255,255,255,0.06)'}}>
                        <p className="text-xl mb-1">{s.icon}</p>
                        <p className="text-xl font-black" style={{color:s.color}}>{s.val}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{s.label}</p>
                      </div>
                    ))}
                  </div>
                )}

                <div className="p-6 space-y-4">
                  <div>
                    <p className="font-black text-gray-800 dark:text-white text-base mb-1">Message Retention</p>
                    <p className="text-xs text-gray-400">Manage storage by archiving or deleting old messages</p>
                  </div>

                  {/* Archive row */}
                  <div className="p-5 rounded-2xl" style={{background:'rgba(37,99,235,0.06)',border:'1.5px solid rgba(37,99,235,0.2)'}}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{background:'rgba(37,99,235,0.15)'}}>
                        <span className="text-lg">📦</span>
                      </div>
                      <div>
                        <p className="font-black text-gray-800 dark:text-white">Archive Messages</p>
                        <p className="text-xs text-gray-400">Soft-hide from chats. Stays in DB, can restore anytime.</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-2 mb-3">
                      {[
                        {val:90,label:'3 Months'},{val:180,label:'6 Months'},
                        {val:365,label:'1 Year'},{val:730,label:'2 Years'},
                        {val:1095,label:'3 Years'},{val:1825,label:'5 Years'},
                        {val:3650,label:'10 Years'},{val:0,label:'🔒 Never'},
                      ].map(p => (
                        <button key={p.val} onClick={() => setArchiveDays(p.val)}
                          className="py-2.5 px-1 rounded-xl text-xs font-bold text-center transition-all"
                          style={archiveDays===p.val
                            ? p.val===0
                              ? {background:'linear-gradient(135deg,#10b981,#059669)',color:'white',boxShadow:'0 4px 12px rgba(16,185,129,0.3)'}
                              : {background:'linear-gradient(135deg,#2563eb,#7c3aed)',color:'white',boxShadow:'0 4px 12px rgba(37,99,235,0.3)'}
                            : p.val===0
                            ? {background:'rgba(16,185,129,0.1)',border:'1.5px solid rgba(16,185,129,0.3)',color:'#10b981'}
                            : {background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.08)',color:'#6b7280'}}>
                          {p.label}
                        </button>
                      ))}
                    </div>
                    <button onClick={()=>{
                        if(archiveDays===0){
                          api('/admin/settings/auto_archive_days',{method:'put',data:{value:0}})
                            .then(()=>toast.success('✅ Archive policy set to Never',{duration:4000}))
                            .catch(()=>{});
                          return;
                        }
                        const period = archiveDays>=3650?`${Math.round(archiveDays/365)} years`:archiveDays>=365?`${Math.round(archiveDays/365)} year(s)`:`${Math.round(archiveDays/30)} months`;
                        setConfirmModal({
                          type:'warning',
                          title:'Archive Messages',
                          subtitle:`Messages older than ${period} will be hidden`,
                          message:`You are about to archive all messages older than ${period}. They will be hidden from all chats but remain in the database and can be restored anytime.`,
                          details:[
                            {icon:'📦',text:'Messages hidden from all conversations'},
                            {icon:'🗄️',text:'Data stays in database — fully restorable'},
                            {icon:'👥',text:'Affects all users and all conversations'},
                            {icon:'📊',text:`Estimated ~${Number(msgStats?.older_180d||0).toLocaleString()} messages affected`},
                          ],
                          confirmLabel:'📦 Archive Now',
                          onConfirm: async () => {
                            setProcessing(true);
                            try {
                              const {data} = await api('/admin/messages/archive', {method:'post', data:{days:archiveDays}});
                              toast.success(data.message);
                              const {data:s} = await api('/admin/messages/stats');
                              setMsgStats(s);
                            } catch(e) { toast.error('Archive failed'); }
                            setProcessing(false);
                          }
                        }); }}
                      className="w-full py-3 text-sm font-black text-white rounded-xl hover:opacity-90 disabled:opacity-50 transition"
                      style={{background:'linear-gradient(135deg,#2563eb,#7c3aed)',boxShadow:'0 4px 15px rgba(37,99,235,0.25)'}}>
                      {processing?'⏳ Processing...':archiveDays===0?'✅ Keep All Messages (Never Archive)':'📦 Archive Messages Older Than Selected Period'}
                    </button>
                  </div>

                  {/* Delete row */}
                  <div className="p-5 rounded-2xl" style={{background:'rgba(239,68,68,0.06)',border:'1.5px solid rgba(239,68,68,0.2)'}}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{background:'rgba(239,68,68,0.15)'}}>
                        <span className="text-lg">🗑️</span>
                      </div>
                      <div>
                        <p className="font-black text-gray-800 dark:text-white">Permanently Delete</p>
                        <p className="text-xs font-semibold" style={{color:'#ef4444'}}>⚠️ Irreversible — cannot be restored once deleted</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-2 mb-3">
                      {[
                        {val:180,label:'6 Months'},{val:365,label:'1 Year'},
                        {val:730,label:'2 Years'},{val:1095,label:'3 Years'},
                        {val:1825,label:'5 Years'},{val:3650,label:'10 Years'},
                        {val:7300,label:'20 Years'},{val:0,label:'🔒 Never'},
                      ].map(p => (
                        <button key={p.val} onClick={() => setDeleteDays(p.val)}
                          className="py-2.5 px-1 rounded-xl text-xs font-bold text-center transition-all"
                          style={deleteDays===p.val
                            ? p.val===0
                              ? {background:'linear-gradient(135deg,#10b981,#059669)',color:'white',boxShadow:'0 4px 12px rgba(16,185,129,0.3)'}
                              : {background:'linear-gradient(135deg,#ef4444,#dc2626)',color:'white',boxShadow:'0 4px 12px rgba(239,68,68,0.3)'}
                            : p.val===0
                            ? {background:'rgba(16,185,129,0.1)',border:'1.5px solid rgba(16,185,129,0.3)',color:'#10b981'}
                            : {background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.08)',color:'#6b7280'}}>
                          {p.label}
                        </button>
                      ))}
                    </div>
                    <button onClick={()=>{
                        if(deleteDays===0){
                          api('/admin/settings/auto_delete_days',{method:'put',data:{value:0}})
                            .then(()=>toast.success('✅ Delete policy set to Never',{duration:4000}))
                            .catch(()=>{});
                          return;
                        }
                        const period = deleteDays>=3650?`${Math.round(deleteDays/365)} years`:deleteDays>=365?`${Math.round(deleteDays/365)} year(s)`:`${Math.round(deleteDays/30)} months`;
                        setConfirmModal({
                          type:'danger',
                          title:'Permanently Delete Messages',
                          subtitle:`⚠️ This action cannot be undone`,
                          message:`You are about to PERMANENTLY DELETE all messages older than ${period}. This is irreversible — deleted messages cannot be recovered.`,
                          details:[
                            {icon:'🗑️',text:'Messages deleted forever — no recovery possible'},
                            {icon:'📎',text:'Associated files and reactions also removed'},
                            {icon:'👥',text:'Affects ALL users and ALL conversations'},
                            {icon:'⚠️',text:'This action is immediate and permanent'},
                          ],
                          confirmLabel:'🗑️ Delete Permanently',
                          onConfirm: async () => {
                            setProcessing(true);
                            try {
                              const {data} = await api('/admin/messages/old', {method:'delete', data:{days:deleteDays}});
                              toast.success(data.message);
                              const {data:s} = await api('/admin/messages/stats');
                              setMsgStats(s);
                            } catch(e) { toast.error('Delete failed'); }
                            setProcessing(false);
                          }
                        }); }}
                      className="w-full py-3 text-sm font-black text-white rounded-xl hover:opacity-90 disabled:opacity-50 transition"
                      style={{background:deleteDays===0?'linear-gradient(135deg,#10b981,#059669)':'linear-gradient(135deg,#ef4444,#dc2626)',boxShadow:`0 4px 15px ${deleteDays===0?'rgba(16,185,129,0.25)':'rgba(239,68,68,0.25)'}`}}>
                      {processing?'⏳ Processing...':deleteDays===0?'✅ Keep All Messages (Never Delete)':'🗑️ Permanently Delete Messages Older Than Selected Period'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ══ AUDIT LOG ══ */}
          {activeTab==='audit' && (
            <div className="space-y-4">
              {/* Filter */}
              <div className="flex gap-2">
                {['all','text','image','file','system'].map(t => (
                  <button key={t} onClick={() => setAuditType(t)}
                    className="px-3 py-1.5 rounded-xl text-xs font-bold transition"
                    style={auditType===t
                      ? {background:'linear-gradient(135deg,#0d9488,#2563eb)',color:'white'}
                      : {background:darkMode?'rgba(255,255,255,0.05)':'white',
                         color:darkMode?'rgba(255,255,255,0.4)':'#6b7280',
                         border:darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid rgba(0,0,0,0.06)'}}>
                    {t.charAt(0).toUpperCase()+t.slice(1)}
                  </button>
                ))}
              </div>

              <div className="rounded-2xl overflow-hidden shadow-sm"
                style={{background:darkMode?'rgba(255,255,255,0.03)':'white',
                  border:darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid rgba(0,0,0,0.06)'}}>
                <table className="w-full">
                  <thead>
                    <tr style={{background:darkMode?'rgba(255,255,255,0.04)':'#f8fafc',
                      borderBottom:darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid rgba(0,0,0,0.06)'}}>
                      {['Time','User','Type','Content / File','Conversation'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-black text-gray-400 uppercase tracking-widest">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {audit.map(a => (
                      <tr key={a.id}
                        className="transition-colors hover:bg-teal-50/20 dark:hover:bg-white/[0.02]"
                        style={{borderBottom:darkMode?'1px solid rgba(255,255,255,0.03)':'1px solid rgba(0,0,0,0.04)'}}>
                        <td className="px-4 py-2.5">
                          <p className="text-xs font-bold text-gray-600 dark:text-gray-300">{format(new Date(a.created_at),'HH:mm')}</p>
                          <p className="text-xs text-gray-400">{format(new Date(a.created_at),'MMM d')}</p>
                        </td>
                        <td className="px-4 py-2.5">
                          <p className="text-xs font-bold text-gray-700 dark:text-gray-200">{a.sender_name}</p>
                          <p className="text-xs text-gray-400">{a.department||a.sender_email}</p>
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="text-xs font-bold px-2 py-0.5 rounded-lg"
                            style={a.message_type==='text'
                              ? {background:'rgba(13,148,136,0.1)',color:'#0d9488'}
                              : a.message_type==='image'
                              ? {background:'rgba(124,58,237,0.1)',color:'#7c3aed'}
                              : a.message_type==='file'
                              ? {background:'rgba(245,158,11,0.1)',color:'#f59e0b'}
                              : {background:'rgba(107,114,128,0.1)',color:'#6b7280'}}>
                            {a.message_type}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 max-w-xs">
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {a.message_type==='text' ? '🔒 Encrypted message'
                              : a.message_type==='image' ? `🔒 Encrypted image (.${(a.file_name?.split('.').pop()||'img').toLowerCase()} · ${formatFileSize(a.file_size)})`
                              : a.message_type==='file' ? `🔒 Encrypted file (.${(a.file_name?.split('.').pop()||'file').toLowerCase()} · ${formatFileSize(a.file_size)})`
                              : a.message_type==='system' ? `⚙️ ${a.content||a.file_name||'System event'}`
                              : '—'}
                          </p>
                        </td>
                        <td className="px-4 py-2.5">
                          <p className="text-xs text-gray-500 dark:text-gray-400">{a.conv_name||'Direct'}</p>
                          <span className="text-xs text-gray-400">{a.conv_type}</span>
                        </td>
                      </tr>
                    ))}
                    {audit.length===0 && (
                      <tr><td colSpan={5} className="text-center py-12 text-gray-400 text-sm">No audit records found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── RESET PASSWORD MODAL ── */}
      {/* ── CUSTOM CONFIRM MODAL ── */}
      {confirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{background:'rgba(0,0,0,0.75)',backdropFilter:'blur(8px)'}}>
          <div className="w-full max-w-md rounded-3xl overflow-hidden shadow-2xl"
            style={{background:'linear-gradient(135deg,#0f172a,#1e293b)',
              border:`1.5px solid ${confirmModal.type==='danger'?'rgba(239,68,68,0.4)':'rgba(37,99,235,0.4)'}`}}>
            <div className="px-6 py-5 flex items-center gap-4"
              style={{background:`linear-gradient(135deg,${confirmModal.type==='danger'?'rgba(239,68,68,0.15),rgba(220,38,38,0.05)':'rgba(37,99,235,0.15),rgba(124,58,237,0.05)'})`}}>
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{background:confirmModal.type==='danger'?'rgba(239,68,68,0.2)':'rgba(37,99,235,0.2)'}}>
                <span className="text-2xl">{confirmModal.type==='danger'?'⚠️':'📦'}</span>
              </div>
              <div>
                <p className="font-black text-white text-base">{confirmModal.title}</p>
                <p className="text-xs mt-0.5" style={{color:confirmModal.type==='danger'?'#fca5a5':'#93c5fd'}}>{confirmModal.subtitle}</p>
              </div>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm text-gray-300 leading-relaxed mb-4">{confirmModal.message}</p>
              {confirmModal.details && (
                <div className="rounded-2xl p-4 space-y-2.5"
                  style={{background:'rgba(255,255,255,0.04)',border:`1px solid ${confirmModal.type==='danger'?'rgba(239,68,68,0.2)':'rgba(37,99,235,0.2)'}`}}>
                  {confirmModal.details.map((d,i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <span className="text-base flex-shrink-0">{d.icon}</span>
                      <span className="text-xs text-gray-300">{d.text}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <button onClick={() => setConfirmModal(null)}
                className="flex-1 py-3 rounded-2xl text-sm font-black transition"
                style={{background:'rgba(255,255,255,0.06)',color:'#94a3b8',border:'1px solid rgba(255,255,255,0.08)'}}>
                Cancel
              </button>
              <button onClick={() => { const fn = confirmModal.onConfirm; setConfirmModal(null); fn(); }}
                className="flex-1 py-3 rounded-2xl text-sm font-black text-white transition hover:opacity-90"
                style={{background:confirmModal.type==='danger'?'linear-gradient(135deg,#ef4444,#dc2626)':'linear-gradient(135deg,#2563eb,#7c3aed)',
                  boxShadow:confirmModal.type==='danger'?'0 4px 20px rgba(239,68,68,0.4)':'0 4px 20px rgba(37,99,235,0.4)'}}>
                {confirmModal.confirmLabel||'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {resetModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setResetModal(null)}>
          <div className="w-full max-w-sm rounded-2xl overflow-hidden shadow-2xl"
            style={{background:darkMode?'#0f172a':'white'}}
            onClick={e=>e.stopPropagation()}>
            <div className="px-6 py-4 flex items-center justify-between" style={{background:'linear-gradient(135deg,#0d9488,#2563eb)'}}>
              <p className="font-black text-white">Reset Password</p>
              <button onClick={() => setResetModal(null)} className="w-7 h-7 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center text-white transition">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-4 h-4"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">Reset password for <strong className="text-gray-800 dark:text-white">{resetModal.full_name}</strong></p>
              <div>
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest block mb-1.5">New Password</label>
                <input type="password" value={newPassword} onChange={e=>setNewPassword(e.target.value)}
                  placeholder="Min 6 characters" autoFocus
                  className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 transition"
                  style={{background:darkMode?'rgba(255,255,255,0.05)':'#f8fafc',
                    border:`1.5px solid ${darkMode?'rgba(255,255,255,0.08)':'#e2e8f0'}`,
                    color:darkMode?'white':'#0f172a'}}/>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setResetModal(null)}
                  className="flex-1 py-2.5 rounded-xl text-sm font-bold text-gray-500 transition"
                  style={{background:darkMode?'rgba(255,255,255,0.05)':'#f1f5f9'}}>Cancel</button>
                <button onClick={resetPassword}
                  className="flex-1 py-2.5 rounded-xl text-sm font-black text-white transition hover:opacity-90"
                  style={{background:'linear-gradient(135deg,#0d9488,#2563eb)'}}>Reset Password</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
